import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';

import { BracketedWrapper } from '@sorare/core/src/atoms/BracketedWrapper/index.tsx';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAlert } from '@sorare/core/src/atoms/icons/system/iconAlert.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, LabelL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_SERIES_SQUAD } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { css } from '@sorare/css';

import { SquadEmblem } from '#src/components/squad/SquadEmblem/index.tsx';
import { SquadMembersCount } from '#src/components/squad/SquadMembersCount/index.tsx';
import { SquadPrivateChip } from '#src/components/squad/SquadPrivateChip/index.tsx';
import { useJoinSquad } from '#src/hooks/squad/useJoinSquad.ts';

import type { JoinSquadDialog_squad } from './__generated__/index.graphql.ts';

const cautionMessage = css`
  width: 100%;
  justify-content: center;
  padding: var(--intermediate-unit);
  background: var(--c-surface-caution);
  border: 1px solid var(--c-yellow-400);
  border-radius: 18px;
`;

const CautionMessage = ({ children }: { children: ReactNode }) => (
  <Horizontal gap={1} center className={cautionMessage}>
    <FontAwesomeIcon icon={iconAlert} color="var(--c-yellow-400)" />
    <LabelL bold color="var(--c-white)">
      {children}
    </LabelL>
  </Horizontal>
);

type ContentProps = {
  squad: JoinSquadDialog_squad;
  onJoined?: () => void;
};

const Content = ({ squad, onJoined }: ContentProps) => {
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const { joinSquad, loading } = useJoinSquad({ showErrorsInForm: true });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isFull = squad.membersCount >= squad.maxMembersCount;

  const handleJoin = async () => {
    const { data, errors } = await joinSquad({ squadId: squad.id });

    if (data?.joinSquad?.currentUser?.squad) {
      if (onJoined) {
        onJoined();
      } else {
        navigate(generateSportContextPath(ANY_SPORT_SERIES_SQUAD));
      }
      return;
    }

    setErrorMessage(errors[0]?.message ?? null);
  };

  return (
    <Vertical gap={2} center className="px-1.5 pb-1.5 pt-3">
      <SquadEmblem level={squad.currentStepLevel} large />
      <Vertical gap={0.5} center>
        <BracketedWrapper
          style={
            { '--border-color': 'var(--c-squads-secondary)' } as CSSProperties
          }
        >
          <BrandL>{squad.name}</BrandL>
        </BracketedWrapper>
        <SquadMembersCount
          membersCount={squad.membersCount}
          maxMembersCount={squad.maxMembersCount}
        />
        {!!squad.private && <SquadPrivateChip />}
      </Vertical>
      {isFull ? (
        <CautionMessage>
          <FormattedMessage defaultMessage="The squad is full" />
        </CautionMessage>
      ) : errorMessage ? (
        <CautionMessage>{errorMessage}</CautionMessage>
      ) : (
        <Button
          size="medium"
          color="primary"
          onClick={handleJoin}
          loading={loading}
        >
          <FormattedMessage defaultMessage="Join Squad" />
        </Button>
      )}
    </Vertical>
  );
};

type Props = {
  squad: JoinSquadDialog_squad | null;
  open: boolean;
  onClose: () => void;
  onJoined?: () => void;
};

export const JoinSquadDialog = ({ squad, open, onClose, onJoined }: Props) => {
  return (
    <Dialog
      maxWidth="xs"
      fullScreen={false}
      fullWidth
      open={open}
      onClose={onClose}
      hideHeader
      blurBackdrop
    >
      {({ CloseButton }) => (
        <div className="relative">
          <div className="absolute right-2 top-2">
            <CloseButton onClose={onClose} />
          </div>
          {squad ? <Content squad={squad} onJoined={onJoined} /> : null}
        </div>
      )}
    </Dialog>
  );
};

JoinSquadDialog.fragments = {
  squad: gql`
    fragment JoinSquadDialog_squad on Squad {
      id
      name
      membersCount
      maxMembersCount
      currentStepLevel
      private
    }
  ` as TypedDocumentNode<JoinSquadDialog_squad>,
};
